import { useQuery } from "@apollo/client"
import { Chip } from "@heroui/react"

import {
  ArrowDownCircleIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid"

import { CircleStackIcon } from "@heroicons/react/24/outline"
import { ErrorPlaceholder, LoadingPlaceholder, SimplePlaceholder } from "@components"
import { GET_STUDENT_GUARDIANS } from "@graphql"
import { guardianFullname, humanReadableDuration, mySqlToLocal } from "@helpers"
import PropTypes from "prop-types"

const StudentGuardiansList = ({ student }) => {
  const { loading, error, data } = useQuery(GET_STUDENT_GUARDIANS, {
    fetchPolicy: "cache-first",
    variables: {
      id: student.id,
    },
  })

  if (loading) return <LoadingPlaceholder caption="Loading guardians..." icon={CircleStackIcon} />

  if (error) return <ErrorPlaceholder caption="There was an error loading the guardians for this student." error={error} />

  if (data.student.guardians.length === 0) {
    return (
      <SimplePlaceholder isFullHeight={false} icon={CircleStackIcon} title="No guardians found for this student">
        <p className="mt-1 text-sm text-content2-foreground">
          If you believe this student should have associated guardians then please check your MIS and Xporter
          integration.
        </p>
      </SimplePlaceholder>
    )
  }

  return (
    <ul>
      {data.student.guardians.filter(guardian => guardian.active).map(guardian => {
        const { contact_sequence, legal_guardian } = guardian.pivot

        const lastSeen = guardian?.user?.last_active
          ? humanReadableDuration(mySqlToLocal(guardian.user.last_active))
          : null

        const activatedDate = guardian?.user?.activated_at
          ? mySqlToLocal(guardian?.user?.activated_at).format("DD/MM/YYYY")
          : "N/A"

        const lastLoginDate = guardian?.user?.last_active
          ? mySqlToLocal(guardian?.user?.last_active).format("DD/MM/YYYY")
          : "N/A"

        return (
          <li key={guardian.id}>
            <div className="flex justify-between pt-3 gap-x-6">
              <div className="flex min-w-0 gap-x-4">
                <div className="flex-auto min-w-0">
                  <h1 className="mb-4 text-lg font-semibold leading-6 text-foreground">{guardianFullname(guardian)}</h1>
                </div>
              </div>

              <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
                {lastSeen && (
                  <Chip
                    startContent={<CheckCircleIcon className="w-6 h-auto text-white" />}
                    color="primary"
                    classNames={{ content: "text-sm font-semibold leading-5" }}
                  >
                    Last seen {lastSeen} ago
                  </Chip>
                )}

                {!lastSeen && (
                  <Chip
                    startContent={<XCircleIcon className="w-6 h-auto text-white" />}
                    color="warning"
                    classNames={{ content: "text-sm font-semibold text-white leading-5" }}
                  >
                    Never Logged In
                  </Chip>
                )}
              </div>
            </div>

            <div>
              <ul className="divide-y divide-gray-300 dark:divide-content2-foreground divide-dotted">
                <li className="flex items-center py-3 leading-5 truncate text-content2-foreground">
                  <PaperAirplaneIcon className="w-6 h-auto mr-2 text-indigo-800 dark:text-foreground" /> {guardian?.user?.email || "N/A"}
                </li>

                <li className="flex items-center py-3 leading-5 truncate text-content2-foreground">
                  <DevicePhoneMobileIcon className="w-6 h-auto mr-2 text-indigo-800 dark:text-foreground" />{" "}
                  {guardian?.user?.mobile || "N/A"}
                </li>

                <li className="flex items-center py-3 leading-5 truncate text-content2-foreground">
                  <ShieldCheckIcon className="w-6 h-auto mr-2 text-indigo-800 dark:text-foreground" /> Account Activated: {activatedDate}
                </li>

                <li className="flex items-center py-3 leading-5 truncate text-content2-foreground">
                  <LockClosedIcon className="w-6 h-auto mr-2 text-indigo-800 dark:text-foreground" /> Last Login: {lastLoginDate}
                </li>
              </ul>
            </div>

            <div className="flex mt-4 mb-6 gap-x-3">
              <Chip startContent={<ArrowDownCircleIcon className="w-6 h-auto" />} color="primary">
                {contact_sequence} # Contact
              </Chip>

              {legal_guardian && (
                <Chip
                  startContent={<CheckCircleIcon className="w-6 h-auto text-white" />}
                  classNames={{ content: "text-white" }}
                  color="success"
                >
                  Legal Guardian
                </Chip>
              )}

              {!legal_guardian && (
                <Chip
                  startContent={<XCircleIcon className="w-6 h-auto text-white" />}
                  classNames={{ content: "text-white" }}
                  color="warning"
                >
                  Legal Guardian
                </Chip>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

StudentGuardiansList.propTypes = {
  student: PropTypes.object.isRequired,
}

export default StudentGuardiansList
